import { forEachSchemaNode } from '@framework/validation/describe/forEachSchemaNode.ts';
import { applyLiveContextTransform } from '@framework/validation/live/applyLiveContextTransform.ts';
import { WARNING_META_KEY } from '@framework/validation/methods/warningMeta.ts';
import { type AnyObject, type ObjectSchema, type Schema } from 'yup';
import { diffStateProps } from './diffStateProps.ts';
import {
  DERIVED_STATE_KEYS,
  type FieldDerivedStateKey,
  type IFieldDerivedState,
} from './IFieldDerivedState.ts';

interface ISubscriber {
  paths: string[];
  /** Chiamato con le proprietà che sono cambiate per i path di interesse */
  callback: (changedProps: Set<FieldDerivedStateKey>) => void;
}

export interface IResolvedSchemaStore {
  /**
   * Ottiene il derived state per un path
   */
  getState(path: string): IFieldDerivedState | null;
  /**
   * Ottiene il context corretto per lo snapshot corrente.
   * Ritorna il context risolto quando disponibile, altrimenti il
   * validationContext raw dell'ultimo resolve.
   */
  getContext(values?: any): any;
  /**
   * Ottiene il solo context risolto dai meta dello schema.
   */
  getResolvedContext(values?: any): any;
  /**
   * Iscrive un listener a uno o più path. Callback chiamato solo se quei path cambiano.
   */
  subscribe(
    paths: string[],
    callback: (changedProps: Set<FieldDerivedStateKey>) => void,
  ): () => void;
  /**
   * Esegue il tree walk completo e aggiorna lo stateMap.
   * Estrae internamente la live context dai meta dello schema partendo dal
   * `validationContext` raw passato dal chiamante.
   * Non notifica i subscriber: chiamare `flushNotifications()` dopo.
   */
  resolve(
    schema: ObjectSchema<AnyObject>,
    values: any,
    validationContext?: any,
  ): void;
  /**
   * Notifica i subscriber dei cambiamenti pendenti dall'ultimo `resolve()`.
   */
  flushNotifications(): void;

  /**
   * Flag per indicare se lo state è "stale" (in attesa di un resolve).
   * Se `true`, i subscriber possono scegliere di attendere prima di leggere lo state,
   * per evitare di leggere valori obsoleti durante un batch di aggiornamenti.
   */
  isStale: boolean;
}

/**
 * Store centralizzato per memorizzare il derived state dei campi (visibilità,
 * editabilità, required, warning).
 *
 * Sostituisce la logica di risoluzione con `reachAndResolve` di `useField`
 * che era inefficiente perché risolveva solo il path specifico.
 */
export class ResolvedSchemaStore implements IResolvedSchemaStore {
  stateMap = new Map<string, IFieldDerivedState>();

  private readonly subscribers = new Set<ISubscriber>();
  private pendingPathChanges: Map<string, Set<FieldDerivedStateKey>> | null =
    null;
  private lastResolvedContext: any = null;
  private lastResolvedValues: any = null;
  private lastResolvedSchema: ObjectSchema<AnyObject> | null = null;
  private lastValidationContext: any = null;

  isStale = false;

  getState(path: string): IFieldDerivedState | null {
    return this.stateMap.get(path) ?? null;
  }

  /**
   * Ritorna il context live per i values dati, senza fallback.
   * - Cache hit (values === ultimi values resolvati): ritorna il context live
   *   già calcolato dal resolve.
   * - Cache miss: ricalcola al volo usando lo schema e il validationContext
   *   dell'ultimo resolve.
   * - Nessun resolve mai eseguito: ritorna `null`.
   */
  getResolvedContext(values?: any): any {
    if (values === undefined || values === this.lastResolvedValues) {
      return this.lastResolvedContext;
    }
    if (!this.lastResolvedSchema) return null;
    return applyLiveContextTransform(
      this.lastResolvedSchema,
      values,
      this.lastValidationContext,
    );
  }

  getContext(values?: any): any {
    return this.getResolvedContext(values) || this.lastValidationContext;
  }

  subscribe(
    paths: string[],
    callback: (changedProps: Set<FieldDerivedStateKey>) => void,
  ): () => void {
    const sub: ISubscriber = { paths, callback };
    this.subscribers.add(sub);
    return () => this.subscribers.delete(sub);
  }

  /**
   * Logica centrale per risolvere lo schema: esegue un tree walk completo e
   * costruisce la nuova mappa di stato.
   *
   * NOTE: In futuro potremmo unificare questo e lo step `schema.cast`
   */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: critical hot path, non si presta a essere semplificato senza introdurre overhead significativo
  /* NOSONAR */ resolve(
    schema: ObjectSchema<AnyObject>,
    values: any,
    validationContext?: any,
  ): void {
    const nextMap = new Map<string, IFieldDerivedState>();
    const liveContext = applyLiveContextTransform(
      schema,
      values,
      validationContext,
    );
    this.lastResolvedValues = values;
    this.lastResolvedContext = liveContext ?? null;
    this.lastResolvedSchema = schema;
    this.lastValidationContext = validationContext;

    try {
      forEachSchemaNode(
        schema,
        { value: values, context: liveContext },
        node => {
          if (!node.path) return;

          // --- Punto Centrale: estrazione dello stato derivato da ogni nodo ---
          const spec = (node.schema as Schema).spec;
          const meta = spec?.meta ?? {};
          // Estrazione inline al posto di chiamare getWarningMetaFromSchema per evitare overhead
          const warning = spec?.meta?.[WARNING_META_KEY] ?? null;

          nextMap.set(node.path, {
            visible: !spec?.strip,
            enabled: meta.enabled !== false,
            layoutEnabled: meta.layoutEnabled !== false,
            required: !spec?.optional,
            warning,
          });
        },
      );
    } catch (e) {
      console.warn('[resolvedSchemaStore] Tree walk failed:', e);
    }

    // Diff: per ogni path cambiato, calcola QUALI proprietà sono cambiate
    const pathChanges = new Map<string, Set<FieldDerivedStateKey>>();

    for (const [path, next] of nextMap) {
      const prev = this.stateMap.get(path);
      const changed = diffStateProps(prev, next);
      if (changed.size > 0) pathChanges.set(path, changed);
    }
    // Path rimossi: tutte le proprietà cambiate
    for (const path of this.stateMap.keys()) {
      if (!nextMap.has(path)) {
        pathChanges.set(path, new Set(DERIVED_STATE_KEYS));
      }
    }

    this.stateMap = nextMap;
    this.pendingPathChanges = pathChanges.size > 0 ? pathChanges : null;
  }

  flushNotifications(): void {
    try {
      const pathChanges = this.pendingPathChanges;
      if (!pathChanges) return;
      this.pendingPathChanges = null;

      for (const sub of this.subscribers) {
        const relevantChanges = new Set<FieldDerivedStateKey>();
        for (const p of sub.paths) {
          const changes = pathChanges.get(p);
          if (changes) {
            for (const c of changes) relevantChanges.add(c);
          }
        }
        if (relevantChanges.size > 0) {
          sub.callback(relevantChanges);
        }
      }
    } finally {
      this.isStale = false; // dopo aver notificato i subscriber, lo state non è più "stale"
    }
  }
}
